import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_PLATFORM_BRANDING,
  fetchPlatformBranding,
  removePlatformAsset,
  renamePlatform,
  resetPlatformBranding,
  uploadPlatformAsset,
  type PlatformBranding,
  type PlatformSlot,
} from "@/api/platformBranding";

export const PLATFORM_BRANDING_KEY = ["platformBranding"] as const;

/** ORAI's own branding. Public, so it loads on the sign-in page as well as inside the app. */
export function usePlatformBranding() {
  return useQuery<PlatformBranding>({
    queryKey: PLATFORM_BRANDING_KEY,
    queryFn: fetchPlatformBranding,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/** The branding to paint right now: the server's answer, or the built-in default until then. */
export function usePlatformBrand(): PlatformBranding {
  return usePlatformBranding().data ?? DEFAULT_PLATFORM_BRANDING;
}

/** Every write answers with the new branding; putting it straight into the cache repaints every
 *  logo on screen at once instead of after a refetch. */
function usePlatformWrite<TArgs>(write: (args: TArgs) => Promise<PlatformBranding>) {
  const queryClient = useQueryClient();
  return useMutation<PlatformBranding, Error, TArgs>({
    mutationFn: write,
    onSuccess: (data) => queryClient.setQueryData(PLATFORM_BRANDING_KEY, data),
  });
}

export function useRenamePlatform() {
  return usePlatformWrite<string | null>(renamePlatform);
}

export function useUploadPlatformAsset() {
  return usePlatformWrite<{ slot: PlatformSlot; file: File }>(({ slot, file }) =>
    uploadPlatformAsset(slot, file),
  );
}

export function useRemovePlatformAsset() {
  return usePlatformWrite<PlatformSlot>(removePlatformAsset);
}

export function useResetPlatformBranding() {
  return usePlatformWrite<void>(() => resetPlatformBranding());
}
